import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialModule } from '../shared-material.module';
import { PdfViewerModule } from 'ng2-pdf-viewer';

// ALWAYS REQUIRED
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { AppLoaderComponent } from '../services/app-loader/app-loader.component';
import { ButtonLoadingComponent } from './button-loading/button-loading.component';
import { HeaderTopComponent } from './header-top/header-top.component';
import { SharedPipesModule } from '../pipes/shared-pipes.modules';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SidenavComponent } from './sidenav/sidenav.component';
import { SidebarSideComponent } from './sidebar-side/sidebar-side.component';
import { SidebarTopComponent } from './sidebar-top/sidebar-top.component';
import { HeaderSideComponent } from './header-side/header-side.component';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { FooterComponent } from './footer/footer.component';
import { SharedDirectivesModule } from '../directives/shared-directives.module';
import { PaginatedAutocompleteComponent } from './paginated-autocomplete/paginated-autocomplete.component';
import { EmbeddedLoaderComponent } from './embedded-loader/embedded-loader.component';
import { FullLoaderComponent } from './full-loader/full-loader.component';
import { PaginatedAutocompleteManualComponent } from './paginated-autocomplete-manual/paginated-autocomplete-manual.component';
import { ProfileSetupNextComponent } from './profile-setup-next/profile-setup-next.component';
import { ChatbotAiComponent } from './chatbot-ai/chatbot-ai.component';
import { UserTermsConditionsComponent } from './user-terms-conditions/user-terms-conditions.component';
import { KycTokenSlotComponent } from '../../views/tokenBasedScreens/kyc-token-slot/kyc-token-slot.component';
import { MpContactDetailsComponent } from './mp-contact-details/mp-contact-details.component';
// Persistent KYC status strip shown in the admin layout while an applicant's
// verification is pending. Declared here (like KycTokenSlotComponent above)
// because its host, AdminLayoutComponent, is declared in this module.
// Author: Akarsh Singh | 04-08-2026
import { KycStatusBannerComponent } from '../../views/dashboard/supplier/kyc-status-banner/kyc-status-banner.component';
import { TrialStripComponent } from './trial-strip/trial-strip.component';
import { TrialEndedAlertComponent } from './trial-ended-alert/trial-ended-alert.component';
// Author: Akarsh Singh | 2026-08-19 — the quotation completeness strip is now
// shared by the supplier quote flow and the buyer RFQ flow, so it is declared
// here rather than inside either feature module.
import { QuoteCompletenessComponent } from '../../views/dashboard/quote/quote-completeness/quote-completeness.component';
// "Already on MPS" notice, shared by the admin and supplier product screens so
// a duplicate is reported the same way wherever it is raised.
import { DuplicateAlertComponent } from './duplicate-alert/duplicate-alert.component';
// Pre-flight review shown when an RFQ is imported from another procurement
// platform, before anything is written.
import { ImportReviewComponent } from './import-review/import-review.component';
// Asks which machine a maker's parts list belongs to, when the sheet does not
// name it on every row.
import { SpareEquipmentPromptComponent } from './spare-equipment-prompt/spare-equipment-prompt.component';
// Shows what an import or a manual create put into the catalogue, and whether
// it is live or still waiting on approval.
import { CatalogueViewComponent } from './catalogue-view/catalogue-view.component';
import { CallNoticeComponent } from './call-notice/call-notice.component';
import { SupplierContactsComponent } from './supplier-contacts/supplier-contacts.component';
import { SupplierPickerComponent } from './supplier-picker/supplier-picker.component';
// "Already on MPS" choice before a supplier is created (2026-10-20)
import { ExistingSupplierChoiceComponent } from './existing-supplier-choice/existing-supplier-choice.component';
import { ComplianceExemptionCardComponent } from './compliance-exemption-card/compliance-exemption-card.component';
// MPS-279 | Akarsh Singh | 2026-08-27 — auto-save status line, used by every
// RFQ creation step.
import { RfqSaveStatusComponent } from './rfq-save-status/rfq-save-status.component';
// Collapsible filter sidebar shared by the search pages | 2026-10-17
import { CollapsibleFilterPanelComponent } from './collapsible-filter-panel/collapsible-filter-panel.component';

const components = [
  AdminLayoutComponent,
  AuthLayoutComponent,
  AppLoaderComponent,
  ButtonLoadingComponent,
  SidenavComponent,
  SidebarSideComponent,
  SidebarTopComponent,
  HeaderTopComponent,
  HeaderSideComponent,
  BreadcrumbComponent,
  FooterComponent,
  PaginatedAutocompleteComponent,
  PaginatedAutocompleteManualComponent,
  EmbeddedLoaderComponent,
  FullLoaderComponent,
  ChatbotAiComponent,
  UserTermsConditionsComponent,
  KycTokenSlotComponent,
  MpContactDetailsComponent,
  KycStatusBannerComponent,
  TrialStripComponent,
  TrialEndedAlertComponent,
  QuoteCompletenessComponent,
  RfqSaveStatusComponent,
  DuplicateAlertComponent,
  ImportReviewComponent,
  SpareEquipmentPromptComponent,
  CatalogueViewComponent,
  CallNoticeComponent,
  SupplierContactsComponent,
  SupplierPickerComponent,
  ExistingSupplierChoiceComponent,
  ComplianceExemptionCardComponent,
  CollapsibleFilterPanelComponent
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    SharedMaterialModule,
    ReactiveFormsModule,
    SharedPipesModule,
    SharedDirectivesModule,
    PdfViewerModule,
    OverlayModule,
    // cdkTrapFocus for the collapsible filter panel's drawer
    A11yModule
  ],
  declarations: [...components, ProfileSetupNextComponent],
  exports: [...components, ProfileSetupNextComponent],
})
export class SharedComponentsModule {}
